/** Estado da simulação — objetos planos, serializáveis e checksummáveis (regra A2). */
import type { SutarifuLevels } from './meta.ts';
import type { Objetivo } from './objectives.ts';
import type { NornoState } from './norno.ts';

export interface SimConfig {
  seed: number;
  chapter: number;
  playerCount: number;
  arenaW: number;
  arenaH: number;
  /** Modo Coragem (ACC-6): −20% densidade, sem elites */
  modoCoragem?: boolean;
  /** níveis dos prédios Sutarifu (M3, AC-M3-05). Ausente = sem bônus (sim base). */
  meta?: SutarifuLevels;
  /** Badalada Sombria (M3, AC-M3-06): opt-in. Ausente/false = zero elites (sim base). */
  badaladaSombria?: boolean;
  /** boss final Norno (M4, AC-M4-04): opt-in. Ausente = capítulo normal (Golemita/objetivo). */
  norno?: boolean;
  /** fragmentos de estrela reunidos pelo perfil (AC-M4-03): decide o desfecho do Norno. */
  fragmentos?: number;
  /** herói por jogador (AC-M4-05): define os especiais → combos. Default: todos 'pip'. */
  heroIds?: string[];
}

export type RunPhase = 'aquecimento' | 'hordas' | 'badalada' | 'guardiao' | 'vitoria' | 'derrota';

export interface HeroSim {
  id: number;
  heroId: string; // pip/luma/zig/nina/gata (roster M4)
  /** id do especial do herói (AC-M4-05): decide o combo com outro herói */
  especialId: string;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  speed: number;
  level: number;
  xp: number;
  /** cooldown da assinatura, em ticks */
  attackCd: number;
  /** i-frames (padrão do gênero): janela pós-dano em que contato não fere — cap global de DPS de contato */
  iframeCd: number;
  alive: boolean;
  /** co-op: "caído" (0 HP mas revivível). down heróis são inertes e ignorados pelos inimigos. */
  down: boolean;
  /** ticks desde que caiu (morre se passar da janela sem revive) */
  downTicks: number;
  /** progresso do revive por um aliado (zera se o aliado se afasta) */
  reviveTicks: number;
  /** carga do especial 0..100 (+1/abate, +0,5/gema; ~90s p/ encher — A.3) */
  special: number;
  kills: number;
  /** armas equipadas (máx 4) — doc 03 §5.1 */
  weapons: OwnedWeapon[];
  /** passivos equipados (máx 4) */
  passives: string[];
  /** fase da órbita das armas orbitais (turns) */
  orbitPhase: number;
  /** auto-revives restantes (Modo Coragem, ACC-6): 1 no modoCoragem, 0 normalmente */
  autoRevives: number;
}

export interface OwnedWeapon {
  id: string;
  level: number; // 1..5
  evolved: boolean;
}

export const MAX_WEAPONS = 4;
export const MAX_PASSIVES = 4;

export interface EnemySim {
  id: number;
  family: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  hp: number;
  maxHp: number;
  /** fase para movimento senoidal (turns) */
  phase: number;
  /** cooldown de dano de contato por inimigo, em ticks */
  contactCd: number;
  alive: boolean;
  /** true se é o Guardião (Golemita) — movimento/dano especiais, vitória ao derrotá-lo */
  boss?: boolean;
  /** true se é um Elite (Badalada Sombria, AC-M3-06) — mais HP/dano e conta p/ o baú */
  elite?: boolean;
}

export type GemTier = 'comum' | 'rara' | 'epica';

export interface GemSim {
  id: number;
  x: number;
  y: number;
  tier: GemTier;
  alive: boolean;
}

export interface HeroInput {
  /** vetor de movimento normalizado [-1,1] (clamp na sim — anti-speedhack) */
  mx: number;
  my: number;
  /** botão do especial (dispara se a carga estiver cheia) */
  special?: boolean;
}

export interface ComboEvent {
  name: string;
  tick: number;
  heroIdxs: number[];
}

export interface RunStats {
  spawned: number;
  kills: number;
  luar: number;
  gemsColetadas: number;
  dropsNaBadalada: number;
  /** elites surgidos na Badalada Sombria (AC-M3-06) — 0 sem opt-in */
  elites: number;
  /** baús garantidos pela Badalada Sombria (AC-M3-06) — 0 sem opt-in */
  chests: number;
}

export interface PendingDraft {
  heroIdx: number;
  cards: import('./draft.ts').Card[];
}

export interface RunState {
  tick: number;
  phase: RunPhase;
  heroes: HeroSim[];
  enemies: EnemySim[];
  gems: GemSim[];
  stats: RunStats;
  /** acumulador de spawn (fração de inimigo "devida") */
  spawnAcc: number;
  nextId: number;
  /** draft aguardando escolha (só no modo interativo; congela a sim até resolveDraft) */
  pendingDraft: PendingDraft | null;
  /** o Guardião (Golemita) foi derrotado? (vitória "de verdade", não só por tempo) */
  bossDefeated: boolean;
  /** rerolls de draft disponíveis na run (1 grátis + Sortudo; AC-M3-05). */
  rerollsDisponiveis: number;
  /** objetivo do capítulo (AC-M3-08): null no cap.1 (fluxo Golemita); mineração/escolta nos cap.2–4. */
  objetivo: Objetivo | null;
  /** estado do boss final Norno (AC-M4-04): null exceto em runs `config.norno`. */
  norno: NornoState | null;
  /** encontro do Palhaço Terrível neste capítulo (AC-M4-02): true nos caps 3/5/7. */
  palhaco: boolean;
  /** Fragmento de Estrela a coletar neste capítulo (AC-M4-03): true nos caps 3/6/9. */
  fragmento: boolean;
  /** tick da última ativação de especial por herói (−1 = nunca) — base da detecção de combo */
  lastSpecialTick: number[];
  /** combos de ultimate disparados (2 especiais < 2s) — doc 03 Apêndice A */
  combos: ComboEvent[];
  /** estado serializado do RNG da run (retomada/replay) */
  rngState: readonly [number, number, number, number];
}

export interface StepOpts {
  /** true no cliente solo: level-up abre draft e PAUSA a sim até a escolha. Bots/headless: autoPick inline. */
  interactive?: boolean;
}

/** Constantes de tempo (doc 08 A.1 + doc 03 §3.2) */
export const TICK_MS = 50; // 20 Hz
export const TICKS_POR_SEGUNDO = 20;
export const DURACAO_RUN_TICKS = 12 * 60 * TICKS_POR_SEGUNDO; // 12 min
export const BADALADA_INICIO_TICK = Math.floor(DURACAO_RUN_TICKS / 2);
export const BADALADA_DURACAO_TICKS = 45 * TICKS_POR_SEGUNDO;
export const GUARDIAO_TICK = 10 * 60 * TICKS_POR_SEGUNDO;
